import { useQuery, useMutation, QueryClient } from "@tanstack/react-query";

import type {
  Subscription,
  Settings,
  Profile,
  Notification,
  BillingState,
  BillingPlan,
  Promo,
  FutureSubscription,
  FutureSubInsight,
  FutureSubAnalysis,
  Overlap,
  FutureSubRecommendation,
  TimelineSimulation,
} from "./types";

import {
  getSubscriptions,
  createSubscription,
  updateSubscription,
  deleteSubscription,
  clearSubscriptions,
} from "./subscriptions";

import { getSettings, updateSettings } from "./settings";
import { getProfile, updateProfile } from "./profile";
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "./notifications";

import { getBilling, getPlans } from "./billing";

import {
  getFutureSubs,
  getFutureSub,
  createFutureSub,
  updateFutureSub,
  deleteFutureSub,
  getAnalysis,
  getTimeline,
  recordDecision,
  getInsights,
} from "./future-subscriptions";

/* ─── Shared QueryClient ─── */

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
    },
  },
});

/* ─── Query Keys ─── */

const subscriptionKeys = {
  all: ["subscriptions"] as const,
  detail: (id: string) => [...subscriptionKeys.all, id] as const,
};

const settingsKeys = {
  all: ["settings"] as const,
};

const profileKeys = {
  all: ["profile"] as const,
};

const notificationKeys = {
  all: ["notifications"] as const,
};

const billingKeys = {
  all: ["billing"] as const,
  plans: ["billing", "plans"] as const,
};

const futureSubKeys = {
  all: ["future-subscriptions"] as const,
  detail: (id: string) => [...futureSubKeys.all, id] as const,
  analysis: (id: string) => [...futureSubKeys.all, id, "analysis"] as const,
  timeline: (id: string) => [...futureSubKeys.all, id, "timeline"] as const,
  insights: ["future-subscriptions-insights"] as const,
};

/* ─── Subscriptions ─── */

export function useSubscriptions() {
  return useQuery({
    queryKey: subscriptionKeys.all,
    queryFn: getSubscriptions,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });
}

export function useSubscription(id: string) {
  return useQuery({
    queryKey: subscriptionKeys.detail(id),
    queryFn: () => {
      const all = queryClient.getQueryData<Subscription[]>(subscriptionKeys.all);
      return all?.find((s) => s.id === id);
    },
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });
}

export function useCreateSubscription() {
  return useMutation({
    mutationFn: createSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
}

export function useUpdateSubscription() {
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Subscription> }) =>
      updateSubscription(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
}

export function useDeleteSubscription() {
  return useMutation({
    mutationFn: deleteSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
}

/* ─── Settings ─── */

export function useSettings() {
  return useQuery({
    queryKey: settingsKeys.all,
    queryFn: getSettings,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
  });
}

export function useUpdateSettings() {
  return useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.all });
    },
  });
}

/* ─── Profile ─── */

export function useProfile() {
  return useQuery({
    queryKey: profileKeys.all,
    queryFn: getProfile,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

/* ─── Notifications ─── */

export function useNotifications() {
  return useQuery({
    queryKey: notificationKeys.all,
    queryFn: getNotifications,
    staleTime: 1000 * 15,
    gcTime: 1000 * 60 * 2,
  });
}

export function useMarkNotificationRead() {
  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllNotificationsRead() {
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/* ─── Billing ─── */

export function useBilling() {
  return useQuery({
    queryKey: billingKeys.all,
    queryFn: getBilling,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });
}

export function usePlans() {
  return useQuery({
    queryKey: billingKeys.plans,
    queryFn: getPlans,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
  });
}

/* ─── Future Subscriptions ─── */

export function useFutureSubs(status?: string, intent?: string) {
  return useQuery({
    queryKey: [...futureSubKeys.all, { status, intent }],
    queryFn: () => getFutureSubs(status, intent),
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });
}

export function useFutureSub(id: string) {
  return useQuery({
    queryKey: futureSubKeys.detail(id),
    queryFn: () => getFutureSub(id),
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });
}

export function useCreateFutureSub() {
  return useMutation({
    mutationFn: createFutureSub,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: futureSubKeys.all });
      queryClient.invalidateQueries({ queryKey: futureSubKeys.insights });
    },
  });
}

export function useUpdateFutureSub() {
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<FutureSubscription> }) =>
      updateFutureSub(id, updates),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: futureSubKeys.all });
      queryClient.invalidateQueries({ queryKey: futureSubKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: futureSubKeys.insights });
    },
  });
}

export function useDeleteFutureSub() {
  return useMutation({
    mutationFn: deleteFutureSub,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: futureSubKeys.all });
      queryClient.invalidateQueries({ queryKey: futureSubKeys.insights });
    },
  });
}

export function useFutureSubInsights() {
  return useQuery({
    queryKey: futureSubKeys.insights,
    queryFn: getInsights,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });
}

export function useFutureSubAnalysis(id: string) {
  return useQuery({
    queryKey: futureSubKeys.analysis(id),
    queryFn: () => getAnalysis(id),
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });
}

export function useFutureSubTimeline(id: string) {
  return useQuery({
    queryKey: futureSubKeys.timeline(id),
    queryFn: () => getTimeline(id),
    enabled: Boolean(id),
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });
}

export function useRecordDecision() {
  return useMutation({
    mutationFn: ({
      id,
      decision,
      rationale,
    }: {
      id: string;
      decision: "buy" | "wait" | "skip" | "replace";
      rationale?: string;
    }) => recordDecision(id, decision, rationale),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: futureSubKeys.all });
      queryClient.invalidateQueries({ queryKey: futureSubKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: futureSubKeys.analysis(variables.id) });
      queryClient.invalidateQueries({ queryKey: futureSubKeys.insights });
    },
  });
}
